import React, { useCallback, useEffect, useId, useState } from 'react';
import { BrowserRouter, NavLink, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { OnlyMeToggle, PrivatePagesProvider, pageKeyFor, usePrivatePages } from './lib/privatePages';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from './lib/supabase';
import { fetchAccessProfile, hasDashboardAccess, canManageAdmins, type AccessProfile } from './lib/accessProfile';
import { LoginScreen } from './components/LoginScreen';
import { HomeScreen } from './pages/HomeScreen';
import { VenueReviewScreen } from './pages/VenueReviewScreen';
import { PhotoApprovalScreen } from './pages/PhotoApprovalScreen';
import { TagApprovalScreen } from './pages/TagApprovalScreen';
import { UsersScreen } from './pages/UsersScreen';
import { CityOverviews } from './pages/CityOverviews';
import { CollectionsScreen } from './pages/CollectionsScreen';
import { SearchPageScreen } from './pages/SearchPageScreen';
import { AnalyticsScreen } from './pages/AnalyticsScreen';
import { EngagementScreen } from './pages/EngagementScreen';
import { GeographyScreen } from './pages/GeographyScreen';
import { ReverseSearchScreen } from './pages/ReverseSearchScreen';
import { AnnouncementsScreen } from './pages/AnnouncementsScreen';
import { FeedbackScreen } from './pages/FeedbackScreen';
import { ReportsScreen } from './pages/ReportsScreen';
import { DuplicatesScreen } from './pages/DuplicatesScreen';
import { ForceUpdateScreen } from './pages/ForceUpdateScreen';
import { FeaturesScreen } from './pages/FeaturesScreen';
import { StrategyScreen } from './pages/StrategyScreen';
import { MockupsScreen } from './pages/MockupsScreen';
import { StudioScreen } from './pages/StudioScreen';
import { MarketingScreen } from './pages/MarketingScreen';
import { OutstandingScreen } from './pages/OutstandingScreen';
import { AiScreen } from './pages/AiScreen';
import { SocialMediaScreen } from './pages/SocialMediaScreen';
import { InfluencerOutreachScreen } from './pages/InfluencerOutreachScreen';
import { VenueRelationsScreen } from './pages/VenueRelationsScreen';
import { GrowthHQScreen } from './pages/GrowthHQScreen';
import { EventsScreen } from './pages/EventsScreen';
import { ReferralsScreen } from './pages/ReferralsScreen';
import { VenueDescriptionsScreen } from './pages/VenueDescriptionsScreen';
import { CampusGrowthScreen } from './pages/CampusGrowthScreen';
import { DevelopmentScreen } from './pages/DevelopmentScreen';
import { useInboxUnreadCount } from './lib/useInboxUnreadCount';
import { useNavBadges } from './lib/useNavBadges';

function AccessDenied({ reason }: { reason: string | null }) {
  useEffect(() => {
    const t = window.setTimeout(() => {
      void supabase.auth.signOut();
    }, 8000);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center px-6 py-10">
      <div className="max-w-md text-center border border-border rounded-xl bg-surface px-8 py-10">
        <h1 className="font-display text-xl font-semibold text-white mb-3 tracking-tight">Access denied</h1>
        <p className="text-muted text-sm leading-relaxed">
          {reason ??
            'You need admin or venue reviewer access on your Shado profile (is_admin or is_venue_reviewer).'}
        </p>
        <p className="text-mutedDim text-xs leading-relaxed mt-4">
          If you recently changed auth settings, confirm Email sign-in is still enabled in Supabase → Authentication
          → Providers. To restore admin access, run{' '}
          <code className="text-muted">supabase/sql/grant_admin_access.sql</code> in the SQL editor with your
          username.
        </p>
        <button
          type="button"
          onClick={() => void supabase.auth.signOut()}
          className="mt-6 rounded-lg border border-border px-4 py-2 text-sm text-white hover:bg-white/[0.04]"
        >
          Back to sign in
        </button>
      </div>
    </div>
  );
}

type NavItem = {
  to: string;
  label: string;
  end?: boolean;
  show: boolean;
  badge?: number;
  tone?: 'accent' | 'amber' | 'red';
  /**
   * Identifies the COUNTER behind `badge`, so a collapsed group's rolled-up total counts one queue
   * once. `/venues` and `/photo-approval` both render `badges.pendingVenuePhotos` — without this
   * the group header would claim double the pending photos there actually are.
   */
  badgeKey?: string;
  /** Set by applyNavLayout when the page appears in more than one category. */
  dup?: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- in-flight nav-groups WIP from parallel session; remove when wired
type NavGroup = { title: string; items: NavItem[] };

/**
 * Evan (2026-08-02): "I want the little [nav] on the sidebar that I can navigate to different
 * pages. I want the bottom section with a bunch of different tabs that I don't really use to be
 * collapsed."
 *
 * Open by default = the groups he works out of daily (Overview + the queues he clears, plus the
 * people/places lookups). Collapsed by default = the rare-use single-item groups and the long
 * growth/content/dev-timeline list. A collapsed group still shows its header, still opens in one
 * click, and still surfaces any pending badge inside it (see `groupBadge`), so nothing can hide.
 */
const NAV_GROUP_DEFAULT_OPEN: Record<string, boolean> = {
  Operations: true,
  Queues: true,
  'People & Places': true,
  Broadcast: false,
  System: false,
  'Growth & Ops': false,
};

/** Same persistence shape as the Overview's `admin:home:*` keys — localStorage + JSON + try/catch. */
const NAV_GROUPS_STORAGE_KEY = 'admin:nav:openGroups';

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- in-flight nav-groups WIP from parallel session; remove when wired
function readStoredNavGroups(): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(NAV_GROUPS_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, boolean>;
    }
    return {};
  } catch {
    return {};
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- in-flight nav-groups WIP from parallel session; remove when wired
function writeStoredNavGroups(next: Record<string, boolean>): void {
  try {
    window.localStorage.setItem(NAV_GROUPS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore persistence failures */
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- in-flight nav-groups WIP from parallel session; remove when wired
function navGroupIsOpen(stored: Record<string, boolean>, title: string): boolean {
  const explicit = stored[title];
  return typeof explicit === 'boolean' ? explicit : (NAV_GROUP_DEFAULT_OPEN[title] ?? true);
}

/**
 * Custom sidebar layout (Evan 2026-09-14): group order, group names (rename/create) and item
 * placement, persisted per browser. `null` = code-defined default. Items are addressed by route
 * path, so a route added in code after a layout was saved is APPENDED (in "More") rather than lost,
 * and a deleted route silently drops out.
 */
type NavLayoutItem = string | { to: string; label?: string };
type NavLayout = {
  groups: { title: string; items: NavLayoutItem[] }[];
  /** Route paths hidden from the sidebar (the "graveyard") — pages stay routable, just unlisted. */
  hidden?: string[];
};
const layoutItemTo = (e: NavLayoutItem): string => (typeof e === 'string' ? e : e.to);
const layoutItemLabel = (e: NavLayoutItem): string | undefined =>
  typeof e === 'string' ? undefined : e.label;
const DASH_BUILD = 'build 2026-11-02e';
const NAV_LAYOUT_STORAGE_KEY = 'admin:nav:layout:v1';

function readStoredNavLayout(): NavLayout | null {
  try {
    const raw = window.localStorage.getItem(NAV_LAYOUT_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === 'object' && Array.isArray((parsed as NavLayout).groups)) {
      return parsed as NavLayout;
    }
    return null;
  } catch {
    return null;
  }
}

/** The layout is SHARED: one row in admin_dash_settings serves every admin login on every
 * device (Evan 2026-10-05: "reflected across all admin account logins in all sessions").
 * localStorage stays only as the boot-time cache so the sidebar never flashes the default. */
const NAV_LAYOUT_DB_KEY = 'nav_layout_v1';
function pushNavLayoutToDb(next: NavLayout | null): void {
  /* ⚠️ supabase-js builders are lazy thenables — without .then NOTHING IS SENT (the beacon
     lesson, 2026-09-22). */
  supabase
    .from('admin_dash_settings')
    .upsert({ key: NAV_LAYOUT_DB_KEY, value: next, updated_at: new Date().toISOString() })
    .then(
      () => {},
      () => {},
    );
}

function writeStoredNavLayout(next: NavLayout | null): void {
  try {
    if (next == null) window.localStorage.removeItem(NAV_LAYOUT_STORAGE_KEY);
    else window.localStorage.setItem(NAV_LAYOUT_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore persistence failures */
  }
}

function applyNavLayout(defaults: NavGroup[], layout: NavLayout | null): NavGroup[] {
  if (!layout) return defaults;
  const byTo = new Map<string, NavItem>();
  defaults.forEach((g) => g.items.forEach((it) => byTo.set(it.to, it)));
  const hidden = new Set(layout.hidden ?? []);
  const used = new Set<string>();
  const counts = new Map<string, number>();
  layout.groups.forEach((g) =>
    g.items.forEach((e) => {
      const to = layoutItemTo(e);
      counts.set(to, (counts.get(to) ?? 0) + 1);
    }),
  );
  const groups: NavGroup[] = layout.groups.map((g) => ({
    title: g.title,
    items: g.items
      .map((e) => {
        const to = layoutItemTo(e);
        used.add(to);
        if (hidden.has(to)) return undefined;
        const it = byTo.get(to);
        if (!it) return undefined;
        const label = layoutItemLabel(e)?.trim();
        const dup = (counts.get(to) ?? 0) > 1;
        return label || dup ? { ...it, ...(label ? { label } : null), ...(dup ? { dup: true } : null) } : it;
      })
      .filter((x): x is NavItem => Boolean(x)),
  }));
  const leftovers: NavItem[] = [];
  defaults.forEach((g) => g.items.forEach((it) => {
    if (!used.has(it.to) && !hidden.has(it.to)) leftovers.push(it);
  }));
  if (leftovers.length > 0) groups.push({ title: 'More', items: leftovers });
  return groups.filter((g) => g.items.length > 0);
}

/**
 * The sidebar editor — reorder groups and pages, rename groups, create new ones. Operates on a
 * draft snapshot; nothing persists until Save.
 */
function NavCustomizer({
  sections,
  defaultLabels,
  hiddenPages,
  onSave,
  onReset,
  onClose,
}: {
  sections: NavGroup[];
  /** the code-defined page names — a custom label is only stored when it differs from these */
  defaultLabels: Map<string, string>;
  /** pages currently in the graveyard (hidden from the sidebar) */
  hiddenPages: { to: string; label: string }[];
  onSave: (layout: NavLayout) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  type DraftGroup = { title: string; items: { to: string; label: string }[] };
  const [draft, setDraft] = useState<DraftGroup[]>(() =>
    sections.map((g) => ({ title: g.title, items: g.items.map((it) => ({ to: it.to, label: it.label })) })),
  );
  const [graveyard, setGraveyard] = useState<{ to: string; label: string }[]>(() => hiddenPages);

  const moveGroup = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= draft.length) return;
    const next = draft.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setDraft(next);
  };
  const moveItem = (gi: number, ii: number, dir: -1 | 1) => {
    const j = ii + dir;
    if (j < 0 || j >= draft[gi].items.length) return;
    const next = draft.map((g) => ({ ...g, items: g.items.slice() }));
    [next[gi].items[ii], next[gi].items[j]] = [next[gi].items[j], next[gi].items[ii]];
    setDraft(next);
  };
  const moveItemToGroup = (gi: number, ii: number, targetGi: number) => {
    if (targetGi === gi) return;
    const next = draft.map((g) => ({ ...g, items: g.items.slice() }));
    const [item] = next[gi].items.splice(ii, 1);
    if (next[targetGi].items.some((x) => x.to === item.to)) {
      next[gi].items.splice(ii, 0, item); /* target already holds this page — keep it where it was */
    } else {
      next[targetGi].items.push(item);
    }
    setDraft(next);
  };

  /** how many copies of a page exist across the whole draft — >1 renders the ⧉ flag */
  const copies = (to: string) => draft.reduce((a, g) => a + g.items.filter((i) => i.to === to).length, 0);

  const [drag, setDrag] = useState<{ gi: number; ii: number } | null>(null);
  const dropAt = (targetGi: number, targetIi: number | null) => {
    if (!drag) return;
    const next = draft.map((g) => ({ ...g, items: g.items.slice() }));
    const [item] = next[drag.gi].items.splice(drag.ii, 1);
    const already = next[targetGi].items.some((x) => x.to === item.to);
    if (already && targetGi !== drag.gi) {
      next[drag.gi].items.splice(drag.ii, 0, item);
    } else {
      const at = targetIi == null ? next[targetGi].items.length : targetIi;
      next[targetGi].items.splice(at, 0, item);
    }
    setDraft(next);
    setDrag(null);
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/70 p-4 sm:p-10"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="admin-card w-full max-w-xl !p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <div className="text-[14px] font-semibold text-textPrimary">Customize navigation</div>
            <div className="text-[11px] text-mutedDim">
              Drag the ≡ handle to move pages anywhere, ⧉ duplicates a page into another category. Saved in this browser.
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onReset}
              className="admin-btn admin-btn-secondary admin-btn-sm whitespace-nowrap shrink-0"
            >
              Reset
            </button>
            <button type="button" onClick={onClose} className="admin-btn admin-btn-secondary admin-btn-sm">
              Cancel
            </button>
            <button
              type="button"
              onClick={() =>
                onSave({
                  groups: draft
                    .map((g) => {
                      /* a page can live in many categories, but only once per category */
                      const seen = new Set<string>();
                      const items: NavLayoutItem[] = [];
                      for (const i of g.items) {
                        if (seen.has(i.to)) continue;
                        seen.add(i.to);
                        const label = i.label.trim();
                        items.push(
                          label && label !== defaultLabels.get(i.to) ? { to: i.to, label } : i.to,
                        );
                      }
                      return { title: g.title.trim() || 'Untitled', items };
                    })
                    .filter((g) => g.items.length > 0 || g.title !== 'Untitled'),
                  hidden: graveyard
                    .map((gp) => gp.to)
                    .filter((to) => !draft.some((g) => g.items.some((i) => i.to === to))),
                })
              }
              className="admin-btn admin-btn-primary admin-btn-sm"
            >
              Save
            </button>
          </div>
        </div>
        <div className="space-y-4">
          {draft.map((g, gi) => (
            <div
              key={gi}
              className="rounded-xl border border-border p-3"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                dropAt(gi, null);
              }}
            >
              <div className="mb-2 flex items-center gap-2">
                <input
                  value={g.title}
                  onChange={(e) => {
                    const next = draft.slice();
                    next[gi] = { ...g, title: e.target.value };
                    setDraft(next);
                  }}
                  className="admin-input !h-7 !w-44 !px-2 !text-[12px] font-semibold"
                />
                <span className="text-[11px] text-mutedDim">{g.items.length} page{g.items.length === 1 ? '' : 's'}</span>
                <div className="ml-auto flex gap-1">
                  <button type="button" onClick={() => moveGroup(gi, -1)} disabled={gi === 0} className="admin-btn admin-btn-secondary admin-btn-sm !px-2 disabled:opacity-30">↑</button>
                  <button type="button" onClick={() => moveGroup(gi, 1)} disabled={gi === draft.length - 1} className="admin-btn admin-btn-secondary admin-btn-sm !px-2 disabled:opacity-30">↓</button>
                  <button
                    type="button"
                    disabled={g.items.length > 0}
                    title={g.items.length > 0 ? 'Move its pages out first' : 'Delete this empty category'}
                    onClick={() => setDraft(draft.filter((_, k) => k !== gi))}
                    className="admin-btn admin-btn-secondary admin-btn-sm !px-2 disabled:opacity-30"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="space-y-1">
                {g.items.map((it, ii) => (
                  <div
                    key={`${it.to}#${ii}`}
                    data-navrow
                    className={`flex items-center gap-2 rounded-md bg-white/[0.03] px-2 py-1 transition-opacity ${
                      drag && drag.gi === gi && drag.ii === ii ? 'opacity-30' : ''
                    }`}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      dropAt(gi, ii);
                    }}
                  >
                    <button
                      type="button"
                      draggable
                      onDragStart={(e) => {
                        setDrag({ gi, ii });
                        e.dataTransfer.effectAllowed = 'move';
                        /* pick up the WHOLE ROW visually, not the browser's tiny handle ghost */
                        const row = (e.currentTarget as HTMLElement).closest('[data-navrow]');
                        if (row instanceof HTMLElement) {
                          e.dataTransfer.setDragImage(row, 24, row.offsetHeight / 2);
                        }
                      }}
                      onDragEnd={() => setDrag(null)}
                      title="Drag to any category or position"
                      className="cursor-grab px-1 text-[13px] leading-none text-mutedDim hover:text-textPrimary"
                    >
                      ≡
                    </button>
                    <input
                      value={it.label}
                      onChange={(e) => {
                        const next = draft.map((g2) => ({ ...g2, items: g2.items.slice() }));
                        next[gi].items[ii] = { ...it, label: e.target.value };
                        setDraft(next);
                      }}
                      title={
                        defaultLabels.get(it.to) && defaultLabels.get(it.to) !== it.label
                          ? `Renamed — default: ${defaultLabels.get(it.to)}`
                          : 'Rename this page'
                      }
                      className="admin-input !h-6 min-w-0 flex-1 !px-2 !text-[12px]"
                    />
                    {copies(it.to) > 1 ? (
                      <span title="Appears in multiple categories" className="shrink-0 text-[10px] text-accent">⧉</span>
                    ) : null}
                    <select
                      value={gi}
                      onChange={(e) => moveItemToGroup(gi, ii, Number(e.target.value))}
                      className="admin-input !h-6 !w-auto !px-1.5 !text-[11px]"
                      title="Move to another category"
                    >
                      {draft.map((g2, k) => (
                        <option key={k} value={k}>{g2.title || 'Untitled'}</option>
                      ))}
                    </select>
                    <button type="button" onClick={() => moveItem(gi, ii, -1)} disabled={ii === 0} className="admin-btn admin-btn-secondary admin-btn-sm !px-2 disabled:opacity-30">↑</button>
                    <button type="button" onClick={() => moveItem(gi, ii, 1)} disabled={ii === g.items.length - 1} className="admin-btn admin-btn-secondary admin-btn-sm !px-2 disabled:opacity-30">↓</button>
                    <button
                      type="button"
                      title="Duplicate into another category (then drag or pick where)"
                      onClick={() => {
                        const next = draft.map((g2) => ({ ...g2, items: g2.items.slice() }));
                        next[gi].items.splice(ii + 1, 0, { ...it });
                        setDraft(next);
                      }}
                      className="admin-btn admin-btn-secondary admin-btn-sm !px-2"
                    >
                      ⧉
                    </button>
                    <button
                      type="button"
                      title={
                        copies(it.to) > 1
                          ? 'Remove this copy (the page keeps its other placement)'
                          : 'Hide this page from the sidebar (moves to the graveyard below)'
                      }
                      onClick={() => {
                        if (
                          copies(it.to) === 1 &&
                          !window.confirm(
                            `Are you sure you want to delete "${it.label}" from the sidebar? Nothing is really deleted — it moves to the graveyard below and can be restored any time.`,
                          )
                        )
                          return;
                        const next = draft.map((g2) => ({ ...g2, items: g2.items.slice() }));
                        next[gi].items.splice(ii, 1);
                        setDraft(next);
                        if (copies(it.to) === 1) {
                          setGraveyard((g2) => [...g2, { to: it.to, label: defaultLabels.get(it.to) ?? it.label }]);
                        }
                      }}
                      className="admin-btn admin-btn-secondary admin-btn-sm !px-2"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                {g.items.length === 0 ? (
                  <div className="px-2 py-1 text-[11px] text-mutedDim">Empty — move pages here with the dropdowns.</div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setDraft([...draft, { title: 'New category', items: [] }])}
          className="admin-btn admin-btn-secondary admin-btn-sm mt-3"
        >
          ＋ New category
        </button>
        {graveyard.length > 0 ? (
          <div className="mt-4 rounded-xl border border-dashed border-border p-3">
            <div className="mb-2 flex items-baseline gap-2">
              <span className="text-[12px] font-semibold text-textPrimary">Graveyard</span>
              <span className="text-[11px] text-mutedDim">
                hidden from the sidebar — the pages themselves still exist
              </span>
            </div>
            <div className="space-y-1">
              {graveyard.map((gp) => (
                <div key={gp.to} className="flex items-center gap-2 rounded-md bg-white/[0.02] px-2 py-1">
                  <span className="min-w-0 flex-1 truncate text-[12px] text-mutedDim">{gp.label}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setGraveyard(graveyard.filter((x) => x.to !== gp.to));
                      const next = draft.map((g2) => ({ ...g2, items: g2.items.slice() }));
                      if (next.length === 0) next.push({ title: 'Restored', items: [] });
                      if (!next[0].items.some((x) => x.to === gp.to)) next[0].items.push({ to: gp.to, label: gp.label });
                      setDraft(next);
                    }}
                    className="admin-btn admin-btn-secondary admin-btn-sm"
                  >
                    Restore
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** `end` items match exactly; the rest also match their sub-paths. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- in-flight nav-groups WIP from parallel session; remove when wired
function navItemIsActive(item: NavItem, pathname: string): boolean {
  if (item.end) return pathname === item.to;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

const BADGE_TONE_RANK: Record<'accent' | 'amber' | 'red', number> = { accent: 0, amber: 1, red: 2 };

/**
 * Rolled-up badge for a collapsed group: one entry per distinct counter, loudest tone wins.
 * A pending queue must never be invisible just because its group is shut.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- in-flight nav-groups WIP from parallel session; remove when wired
function groupBadge(items: NavItem[]): { count: number; tone: 'accent' | 'amber' | 'red' } | null {
  const counted = new Set<string>();
  let count = 0;
  let tone: 'accent' | 'amber' | 'red' = 'accent';
  for (const it of items) {
    if (!it.show || !it.badge || it.badge <= 0) continue;
    const key = it.badgeKey ?? it.to;
    if (counted.has(key)) continue;
    counted.add(key);
    count += it.badge;
    const t = it.tone ?? 'accent';
    if (BADGE_TONE_RANK[t] > BADGE_TONE_RANK[tone]) tone = t;
  }
  return count > 0 ? { count, tone } : null;
}

function FullScreenLoader({ label }: { label: string }) {
  return (
    <div className="min-h-screen bg-canvas flex flex-col items-center justify-center gap-3">
      <span className="h-5 w-5 animate-spin rounded-full border-2 border-borderStrong border-t-accent" aria-hidden />
      <span className="text-[12px] uppercase tracking-[0.08em] text-mutedDim">{label}</span>
    </div>
  );
}

function initialsFromEmail(email?: string | null): string {
  const base = (email ?? '?').split('@')[0];
  return base.slice(0, 2).toUpperCase();
}

const PAGE_TITLES: Record<string, string> = {
  '/': 'Overview',
  '/venues': 'Venues',
  '/photo-approval': 'Photo Approval',
  '/tag-approval': 'Tag Approval',
  '/reverse-search': 'Foursquare',
  '/feedback': 'Feedback',
  '/reports': 'Reports',
  '/cities': 'Cities',
  '/collections': 'Collections',
  '/search-page': 'Search Page',
  '/analytics': 'Analytics',
  '/engagement': 'Engagement',
  '/geography': 'Geography',
  '/users': 'Users',
  '/referrals': 'Referrals',
  '/duplicates': 'Duplicates',
  '/announcements': 'Announcements',
  '/features': 'Features',
  '/force-update': 'Force Update',
  '/strategy': 'Strategy',
  '/mockups': 'Mockups',
  '/studio': 'Studio',
  '/marketing': 'Marketing',
  '/social-media': 'Social Media',
  '/influencer-outreach': 'Influencer Outreach',
  '/venue-relations': 'Venue Relations',
  '/growth': 'Growth HQ',
  '/events': 'Events',
  '/venue-descriptions': 'Descriptions',
  '/campus-growth': 'Campus Growth',
  '/development': 'Development',
  '/outstanding': 'Outstanding',
};

/** Shared nav-items renderer — used by both the desktop `<aside>` and the mobile slide-in overlay
 * so the section/item list is defined once (`SidebarNav`'s `sections`).
 *
 * Every group header is a disclosure button. Both copies of this component are mounted at once on
 * mobile, so the `aria-controls` target ids are namespaced with `useId` rather than the title. */
function NavSections({
  sections,
  badgeClass,
  openGroups,
  onToggleGroup,
  onNavigate,
  linkClassName,
  onCustomize,
}: {
  sections: NavGroup[];
  badgeClass: (tone: 'accent' | 'amber' | 'red') => string;
  openGroups: Record<string, boolean>;
  onToggleGroup: (title: string) => void;
  onNavigate?: () => void;
  linkClassName?: string;
  onCustomize?: () => void;
}) {
  const idBase = useId();
  const { isHidden, isPrivate, isMaster } = usePrivatePages();
  return (
    <>
      {sections.map((sec, i) => {
        // Master-admin private pages vanish from everyone else's sidebar (Evan 2026-11-02).
        const visible = sec.items.filter((it) => it.show && !isHidden(it.to));
        if (visible.length === 0) return null;
        const open = navGroupIsOpen(openGroups, sec.title);
        const rolled = open ? null : groupBadge(visible);
        const panelId = `${idBase}-nav-group-${i}`;
        return (
          <div key={sec.title}>
            <button
              type="button"
              onClick={() => onToggleGroup(sec.title)}
              aria-expanded={open}
              aria-controls={panelId}
              className="admin-section-toggle"
            >
              <span className="admin-section-caret" data-open={open} aria-hidden>
                ›
              </span>
              <span className="min-w-0 flex-1 truncate text-left">{sec.title}</span>
              {rolled ? (
                <span className={badgeClass(rolled.tone)}>{rolled.count > 99 ? '99+' : rolled.count}</span>
              ) : null}
            </button>
            {open ? (
              <div id={panelId}>
                {visible.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.end}
                    onClick={onNavigate}
                    className={linkClassName ?? 'admin-nav-link'}
                  >
                    {({ isActive }) => (
                      <>
                        {isActive ? <span className="admin-nav-rail" /> : null}
                        <span>{it.label}</span>
                        {isMaster && isPrivate(it.to) ? (
                          <span title="Only you can see this page" className="text-[9px] text-accent" aria-label="private">
                            ●
                          </span>
                        ) : null}
                        {it.dup ? (
                          <span title="Also appears in another category" className="text-[9px] text-mutedDim">⧉</span>
                        ) : null}
                        {it.badge && it.badge > 0 ? (
                          <span className={badgeClass(it.tone ?? 'accent')}>{it.badge > 99 ? '99+' : it.badge}</span>
                        ) : null}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
      {onCustomize ? (
        <button
          type="button"
          onClick={onCustomize}
          className="mt-2 w-full px-5 py-1.5 text-left text-[10.5px] font-medium uppercase tracking-[0.08em] text-mutedDim transition-colors hover:text-textPrimary"
        >
          ✎ Customize navigation
        </button>
      ) : null}
    </>
  );
}

function SidebarNav({
  access,
  user,
  mobileOpen,
  onCloseMobile,
}: {
  access: AccessProfile;
  user: User;
  mobileOpen: boolean;
  onCloseMobile: () => void;
}) {
  const { count: unreadFeedback } = useInboxUnreadCount();
  const badges = useNavBadges(access.isAdmin);
  const { pathname } = useLocation();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => readStoredNavGroups());
  const [navLayout, setNavLayout] = useState<NavLayout | null>(() => readStoredNavLayout());
  const [customizing, setCustomizing] = useState(false);

  /* Pull the shared layout once per mount; the server is the truth, localStorage the cache.
     No shared row yet → migrate this browser's local layout up so nothing is lost. */
  useEffect(() => {
    supabase
      .from('admin_dash_settings')
      .select('value')
      .eq('key', NAV_LAYOUT_DB_KEY)
      .maybeSingle()
      .then(
        ({ data }) => {
          if (data && 'value' in data) {
            const remote = ((data as { value: unknown }).value ?? null) as NavLayout | null;
            setNavLayout(remote);
            writeStoredNavLayout(remote);
          } else {
            const local = readStoredNavLayout();
            if (local) pushNavLayoutToDb(local);
          }
        },
        () => {
          /* offline or RLS hiccup — the local cache keeps working */
        },
      );
  }, []);

  const toggleGroup = useCallback((title: string) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [title]: !navGroupIsOpen(prev, title) };
      writeStoredNavGroups(next);
      return next;
    });
  }, []);

  const badgeClass = (tone: 'accent' | 'amber' | 'red') =>
    tone === 'red'
      ? 'admin-badge admin-badge-danger'
      : tone === 'amber'
        ? 'admin-badge admin-badge-warning'
        : 'admin-badge admin-badge-accent';

  // Escape closes the mobile overlay.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseMobile();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen, onCloseMobile]);

  const sections: NavGroup[] = [
    {
      title: 'Operations',
      items: [
        { to: '/', label: 'Overview', end: true, show: access.isAdmin },
        { to: '/outstanding', label: 'Outstanding', show: access.isAdmin },
        /* Operations, not Queues: the AI tab is a watch-the-system page, not a work queue — it
           observes and never writes. Feedback on AI answers lives inside it rather than in the
           Feedback tab because it is a labelled dataset about one feature, not a support inbox. */
        { to: '/ai', label: 'AI', show: access.isAdmin },
      ],
    },
    {
      title: 'Queues',
      items: [
        {
          to: '/venues',
          label: 'Venues',
          show: true,
          badge: badges.pendingVenuePhotos,
          tone: 'amber',
          badgeKey: 'pendingVenuePhotos',
        },
        {
          to: '/photo-approval',
          label: 'Photo Approval',
          show: access.isAdmin,
          badge: badges.pendingVenuePhotos,
          tone: 'amber',
          badgeKey: 'pendingVenuePhotos',
        },
        /* Directly under Photo Approval on Evan's instruction (2026-09-09) — it is the same job
           on a different submission type: a user-supplied thing waiting to become public. */
        {
          to: '/tag-approval',
          label: 'Tag Approval',
          show: access.isAdmin,
          badge: badges.pendingTags,
          tone: 'amber',
          badgeKey: 'pendingTags',
        },
        { to: '/reverse-search', label: 'Foursquare', show: access.isAdmin, badge: badges.pendingQueue, tone: 'amber' },
        { to: '/duplicates', label: 'Duplicates', show: access.isAdmin },
        /* AI-search-generated venue descriptions awaiting approval, plus the hand-written path.
           Lives in Queues because the default view IS a review queue. */
        { to: '/venue-descriptions', label: 'Descriptions', show: access.isAdmin },
        { to: '/feedback', label: 'Feedback', show: access.isAdmin, badge: unreadFeedback, tone: 'accent' },
        { to: '/reports', label: 'Reports', show: access.isAdmin, badge: badges.openReports, tone: 'red' },
      ],
    },
    {
      title: 'People & Places',
      items: [
        { to: '/users', label: 'Users', show: access.isAdmin },
        { to: '/referrals', label: 'Referrals', show: access.isAdmin },
        { to: '/cities', label: 'Cities', show: true },
        /* Next to Cities on purpose: a collection's scope IS a city — all the live ones follow the
           reader's city, so the two pages answer the same question from opposite ends.
           One entry, not two: Collections is the Search page's first section, and `/collections`
           stays routable underneath for anything already linking to it. */
        { to: '/search-page', label: 'Search Page', show: access.isAdmin },
        { to: '/analytics', label: 'Analytics', show: access.isAdmin },
        /* Sits next to Analytics on purpose: Analytics answers "how much are people rating",
           Engagement answers "are they coming back" — same data, opposite question. */
        { to: '/engagement', label: 'Engagement', show: access.isAdmin },
        { to: '/geography', label: 'Geography', show: access.isAdmin },
      ],
    },
    { title: 'Broadcast', items: [{ to: '/announcements', label: 'Announcements', show: access.isAdmin }] },
    {
      title: 'System',
      items: [
        { to: '/features', label: 'Features', show: access.isAdmin },
        { to: '/force-update', label: 'Force Update', show: access.isAdmin },
      ],
    },
    {
      title: 'Growth & Ops',
      items: [
        { to: '/development', label: 'Development', show: access.isAdmin },
        { to: '/strategy', label: 'Strategy', show: access.isAdmin },
        { to: '/mockups', label: 'Mockups', show: access.isAdmin },
        { to: '/studio', label: 'Studio', show: access.isAdmin },
        { to: '/marketing', label: 'Marketing', show: access.isAdmin },
        { to: '/social-media', label: 'Social Media', show: access.isAdmin },
        { to: '/influencer-outreach', label: 'Influencer Outreach', show: access.isAdmin },
        { to: '/venue-relations', label: 'Venue Relations', show: access.isAdmin },
        { to: '/growth', label: 'Growth HQ', show: access.isAdmin },
        { to: '/events', label: 'Events', show: access.isAdmin },
        { to: '/campus-growth', label: 'Campus Growth', show: access.isAdmin },
      ],
    },
  ];

  /**
   * Navigating into a collapsed group opens it. Without this the sidebar gives no indication of
   * where you are — the active item would be inside a shut group. Plain `const`, not `useMemo`, so
   * the effect below can depend on the title string alone rather than on `sections` (rebuilt every
   * render); the state updater no-ops when the group is already open, so React bails out.
   */
  const effectiveSections = applyNavLayout(sections, navLayout);

  const activeGroupTitle =
    effectiveSections.find((g) => g.items.some((it) => it.show && navItemIsActive(it, pathname)))?.title ?? null;

  useEffect(() => {
    if (!activeGroupTitle) return;
    setOpenGroups((prev) => {
      if (navGroupIsOpen(prev, activeGroupTitle)) return prev;
      const next = { ...prev, [activeGroupTitle]: true };
      writeStoredNavGroups(next);
      return next;
    });
  }, [activeGroupTitle]);

  const brand = (
    <div className="px-5 py-5 border-b border-border">
      {/* the real traced wordmark (white vector) — same asset the studios embed */}
      <img src="/shado-wordmark.svg" alt="Shado" className="h-[22px] w-auto" draggable={false} />
      <div className="mt-2 text-[10px] uppercase tracking-[0.14em] text-mutedDim">Admin</div>
      {/* Visible build stamp (2026-09-22): a long-lived tab serving a stale bundle is
          indistinguishable from a shipped fix without one — the studios learned this twice. */}
      <div className="mt-0.5 text-[9px] tracking-[0.08em] text-mutedDim/60">{DASH_BUILD}</div>
    </div>
  );

  const profileFooter = (
    <div className="border-t border-border p-3 flex items-center gap-2.5">
      <span className="grid h-8 w-8 place-items-center rounded-full bg-[linear-gradient(135deg,#8B5CF6,#6D28D9)] text-[11px] font-semibold text-white">
        {initialsFromEmail(user.email)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[12px] text-textPrimary">{user.email ?? 'Operator'}</div>
        <div className="text-[10px] uppercase tracking-[0.08em] text-mutedDim">
          {access.isAdmin ? 'Admin' : 'Reviewer'}
        </div>
      </div>
      <button
        type="button"
        onClick={() => void supabase.auth.signOut()}
        title="Sign out"
        aria-label="Sign out"
        className="admin-btn admin-btn-ghost admin-btn-sm px-2 h-10 lg:h-7"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <path
            d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );

  return (
    <>
      {/* Desktop — permanent sidebar, unchanged from before. */}
      <aside className="hidden lg:flex w-[16.5rem] shrink-0 bg-canvas border-r border-border flex-col min-h-screen">
        {brand}
        <nav className="flex-1 overflow-y-auto py-2" aria-label="Main">
          <NavSections
            sections={effectiveSections}
            badgeClass={badgeClass}
            openGroups={openGroups}
            onToggleGroup={toggleGroup}
            onCustomize={() => setCustomizing(true)}
          />
        </nav>
        {profileFooter}
      </aside>

      {/* Mobile / tablet (<lg) — slide-in overlay with scrim, triggered by the header hamburger. */}
      {mobileOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"
            onClick={onCloseMobile}
            aria-hidden="true"
          />
          <aside
            className="absolute inset-y-0 left-0 flex w-[16.5rem] max-w-[85vw] flex-col overflow-y-auto border-r border-border bg-canvas shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-label="Main navigation"
          >
            {brand}
            <nav className="flex-1 overflow-y-auto py-2" aria-label="Main">
              <NavSections
                sections={effectiveSections}
                badgeClass={badgeClass}
                openGroups={openGroups}
                onToggleGroup={toggleGroup}
                onNavigate={onCloseMobile}
                linkClassName="admin-nav-link admin-nav-link--touch"
                onCustomize={() => setCustomizing(true)}
              />
            </nav>
            {profileFooter}
          </aside>
        </div>
      ) : null}

      {customizing ? (
        <NavCustomizer
          sections={effectiveSections}
          defaultLabels={new Map(sections.flatMap((g) => g.items.map((it) => [it.to, it.label] as [string, string])))}
          hiddenPages={(navLayout?.hidden ?? []).map((to) => ({
            to,
            label:
              sections.flatMap((g) => g.items).find((it) => it.to === to)?.label ?? to,
          }))}
          onSave={(layout) => {
            writeStoredNavLayout(layout);
            pushNavLayoutToDb(layout);
            setNavLayout(layout);
            setCustomizing(false);
          }}
          onReset={() => {
            writeStoredNavLayout(null);
            pushNavLayoutToDb(null);
            setNavLayout(null);
            setCustomizing(false);
          }}
          onClose={() => setCustomizing(false)}
        />
      ) : null}
    </>
  );
}

function DashboardShell({ access, user }: { access: AccessProfile; user: User }) {
  const { pathname } = useLocation();
  const title = PAGE_TITLES[pathname] ?? 'Admin';
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    document.title = `${title} · Shado Admin`;
  }, [title]);

  // Route change (including a tap on a nav link) always closes the mobile overlay.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  return (
    <PrivatePagesProvider>
    <div className="flex min-h-screen bg-canvas">
      <SidebarNav access={access} user={user} mobileOpen={mobileNavOpen} onCloseMobile={() => setMobileNavOpen(false)} />
      <div className="flex min-w-0 flex-1 flex-col min-h-screen">
        <div className="sticky top-0 z-20 flex h-11 shrink-0 items-center justify-between gap-2 border-b border-border bg-canvas/90 px-3 sm:px-6 backdrop-blur">
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open menu"
              className="-ml-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-textPrimary hover:bg-white/[0.04] lg:hidden"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
                <path d="M3 6h18M3 12h18M3 18h18" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div className="flex min-w-0 items-center gap-1.5 text-[13px]">
              <span className="text-mutedDim">Shado</span>
              <span className="text-mutedDim">/</span>
              <span className="truncate font-medium text-textPrimary">{title}</span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <OnlyMeToggle pageKey={pageKeyFor(pathname)} />
            <div id="admin-topbar-actions" className="flex items-center gap-2" />
            <span className="hidden h-4 w-px bg-border sm:block" />
            <span className="hidden items-center gap-1.5 rounded-md border border-borderStrong bg-surfaceElevated px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#E3B341] sm:inline-flex">
              <span className="admin-live-dot" />
              Production
            </span>
            <span className="grid h-5 w-5 place-items-center rounded-full bg-surfaceElevated text-[10px] text-muted">
              {initialsFromEmail(user.email)}
            </span>
          </div>
        </div>
        <div className="min-w-0 flex-1 overflow-auto">
          <PrivateGate pathname={pathname}>
            <Outlet context={{ access, user }} />
          </PrivateGate>
        </div>
      </div>
    </div>
    </PrivatePagesProvider>
  );
}

/** Blocks a master-only page for everyone else, including by typed URL. Renders nothing until the
 *  private list has loaded so a private page never flashes. */
function PrivateGate({ pathname, children }: { pathname: string; children: React.ReactNode }) {
  const { ready, isHidden } = usePrivatePages();
  if (!ready) return null;
  if (isHidden(pageKeyFor(pathname))) {
    return (
      <div className="mx-auto max-w-md px-6 py-24 text-center">
        <div className="text-[15px] font-semibold text-textPrimary">This page is private</div>
        <div className="mt-1 text-[12.5px] text-mutedDim">It&rsquo;s only visible to the account that owns it.</div>
      </div>
    );
  }
  return <>{children}</>;
}

/** One-time carry-over of localStorage from shado-admin.vercel.app when the dash is opened on
 * admin.joinshado.com. localStorage is per-origin, so the custom domain starts with none of the
 * sidebar layout, studio working drafts, or custom templates — this pulls them all over through a
 * hidden iframe (public/ls-bridge.html) the first time, never overwriting a key that already
 * exists here, then reloads once so the nav and studios pick everything up. */
const LS_MIGRATED_KEY = 'admin:ls-migrated:v1';
function useLocalStorageCarryOver() {
  useEffect(() => {
    if (window.location.hostname !== 'admin.joinshado.com') return;
    try {
      if (localStorage.getItem(LS_MIGRATED_KEY)) return;
      /* belt: once per TAB no matter what — sessionStorage survives reloads, so even a
         pathological storage state can never reload-loop */
      if (sessionStorage.getItem('admin:ls-carryover-ran')) return;
      sessionStorage.setItem('admin:ls-carryover-ran', '1');
    } catch {
      return;
    }
    const SOURCE = 'https://shado-admin.vercel.app';
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = `${SOURCE}/ls-bridge.html`;
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      window.removeEventListener('message', onMsg);
      iframe.remove();
    };
    const onMsg = (e: MessageEvent) => {
      if (e.origin !== SOURCE) return;
      const d = e.data as { ns?: string; data?: Record<string, string> } | null;
      if (!d || d.ns !== 'shado-ls-bridge' || !d.data) return;
      /* ⚠️ FLAG FIRST, AND NEVER RELOAD ON A DIRTY COPY. The original wrote the flag AFTER the
         copy loop and reloaded whenever anything was copied — so a quota failure mid-copy left
         the flag unset with copied > 0, and the page reloaded into the same state forever. The
         dash was usable for seconds between reloads, which surfaced as "clicking Approve
         refreshes the whole dash to Verifying access and nothing saves" (Evan, 2026-09-21). */
      try {
        localStorage.setItem(LS_MIGRATED_KEY, new Date().toISOString());
      } catch {
        finish();
        return; /* can't even write the flag — storage is full or blocked; do nothing, never loop */
      }
      let copied = 0;
      let failed = 0;
      for (const [k, v] of Object.entries(d.data)) {
        if (k === LS_MIGRATED_KEY || typeof v !== 'string') continue;
        try {
          if (localStorage.getItem(k) === null) {
            localStorage.setItem(k, v);
            copied += 1;
          }
        } catch {
          failed += 1; /* quota — skip this key, keep going; a partial carry-over beats a loop */
        }
      }
      finish();
      if (copied > 0 && failed === 0) window.location.reload();
    };
    window.addEventListener('message', onMsg);
    document.body.appendChild(iframe);
    const timer = window.setTimeout(finish, 8000);
    return () => {
      window.clearTimeout(timer);
      finish();
    };
  }, []);
}

export default function App() {
  useLocalStorageCarryOver();
  const [session, setSession] = useState<Session | null>(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [access, setAccess] = useState<AccessProfile | null>(null);
  const [accessState, setAccessState] = useState<'idle' | 'checking' | 'ok' | 'denied'>('idle');
  const [accessDeniedReason, setAccessDeniedReason] = useState<string | null>(null);

  const refreshAccess = useCallback(async (userId: string) => {
    setAccessState('checking');
    setAccessDeniedReason(null);
    try {
      const profile = await fetchAccessProfile(userId);
      if (!hasDashboardAccess(profile)) {
        setAccess(null);
        setAccessDeniedReason(
          'Signed in successfully, but your profile does not have admin or venue reviewer access. Ask another admin to enable is_admin, or run grant_admin_access.sql in Supabase.',
        );
        setAccessState('denied');
        return;
      }
      setAccess(profile);
      setAccessState('ok');
    } catch (e: unknown) {
      setAccess(null);
      const msg = e instanceof Error ? e.message : 'Could not load profile';
      setAccessDeniedReason(
        msg === 'Profile not found'
          ? 'Signed in, but no profiles row exists for this account. The main app may not have finished signup, or RLS is blocking profile reads.'
          : `Signed in, but access check failed: ${msg}`,
      );
      setAccessState('denied');
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (!mounted) return;
      setSession(s ?? null);
      setSessionReady(true);
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s ?? null);
    });
    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const uid = session?.user?.id;
    if (!uid) {
      setAccess(null);
      setAccessState('idle');
      return;
    }
    void refreshAccess(uid);
  }, [session?.user?.id, refreshAccess]);

  if (!sessionReady) {
    return <FullScreenLoader label="Loading" />;
  }

  if (!session) {
    return <LoginScreen />;
  }

  if ((accessState === 'checking' || accessState === 'idle') && !access) {
    /* Block only when we have never verified this user. A re-check with a profile already in
       hand keeps the dashboard mounted — a transient auth flap must not blank in-flight work. */
    return <FullScreenLoader label="Verifying access" />;
  }

  if (accessState === 'denied' || !access) {
    return <AccessDenied reason={accessDeniedReason} />;
  }

  const user = session.user;

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<DashboardShell access={access} user={user} />}>
          <Route
            index
            element={access.isAdmin ? <HomeScreen /> : <Navigate to="/venues" replace />}
          />
          <Route path="venues" element={<VenueReviewScreen user={user} />} />
          <Route
            path="photo-approval"
            element={access.isAdmin ? <PhotoApprovalScreen user={user} /> : <Navigate to="/venues" replace />}
          />
          <Route
            path="tag-approval"
            element={access.isAdmin ? <TagApprovalScreen /> : <Navigate to="/venues" replace />}
          />
          {/* Re-Crop was folded into Photo Approval on 2026-08-02 — keep old bookmarks working. */}
          <Route path="recrop" element={<Navigate to="/photo-approval" replace />} />
          <Route
            path="feedback"
            element={access.isAdmin ? <FeedbackScreen /> : <Navigate to="/venues" replace />}
          />
          <Route
            path="reports"
            element={access.isAdmin ? <ReportsScreen /> : <Navigate to="/venues" replace />}
          />
          <Route
            path="referrals"
            element={access.isAdmin ? <ReferralsScreen /> : <Navigate to="/venues" replace />}
          />
          <Route
            path="users"
            element={
              access.isAdmin ? (
                <UsersScreen canManageAdmins={canManageAdmins(access)} />
              ) : (
                <Navigate to="/venues" replace />
              )
            }
          />
          <Route path="cities" element={<CityOverviews canEditCityFilters={access.isAdmin} user={user} />} />
          <Route
            path="search-page"
            element={access.isAdmin ? <SearchPageScreen /> : <Navigate to="/venues" replace />}
          />
          {/* Kept for existing links; the sidebar points at /search-page, whose first section this is. */}
          <Route
            path="collections"
            element={access.isAdmin ? <CollectionsScreen /> : <Navigate to="/venues" replace />}
          />
          <Route
            path="analytics"
            element={access.isAdmin ? <AnalyticsScreen /> : <Navigate to="/venues" replace />}
          />
          <Route
            path="engagement"
            element={access.isAdmin ? <EngagementScreen /> : <Navigate to="/venues" replace />}
          />
          <Route
            path="geography"
            element={access.isAdmin ? <GeographyScreen /> : <Navigate to="/venues" replace />}
          />
          <Route
            path="reverse-search"
            element={access.isAdmin ? <ReverseSearchScreen /> : <Navigate to="/venues" replace />}
          />
          <Route
            path="announcements"
            element={access.isAdmin ? <AnnouncementsScreen /> : <Navigate to="/venues" replace />}
          />
          <Route
            path="duplicates"
            element={access.isAdmin ? <DuplicatesScreen /> : <Navigate to="/venues" replace />}
          />
          <Route
            path="features"
            element={access.isAdmin ? <FeaturesScreen /> : <Navigate to="/venues" replace />}
          />
          <Route
            path="force-update"
            element={access.isAdmin ? <ForceUpdateScreen /> : <Navigate to="/venues" replace />}
          />
          <Route
            path="strategy"
            element={access.isAdmin ? <StrategyScreen /> : <Navigate to="/venues" replace />}
          />
          <Route
            path="mockups"
            element={access.isAdmin ? <MockupsScreen /> : <Navigate to="/venues" replace />}
          />
          <Route
            path="studio"
            element={access.isAdmin ? <StudioScreen /> : <Navigate to="/venues" replace />}
          />
          <Route
            path="marketing"
            element={access.isAdmin ? <MarketingScreen /> : <Navigate to="/venues" replace />}
          />
          <Route
            path="outstanding"
            element={access.isAdmin ? <OutstandingScreen /> : <Navigate to="/venues" replace />}
          />
          <Route
            path="ai"
            element={access.isAdmin ? <AiScreen /> : <Navigate to="/venues" replace />}
          />
          <Route
            path="social-media"
            element={access.isAdmin ? <SocialMediaScreen /> : <Navigate to="/venues" replace />}
          />
          <Route
            path="influencer-outreach"
            element={access.isAdmin ? <InfluencerOutreachScreen /> : <Navigate to="/venues" replace />}
          />
          <Route
            path="venue-relations"
            element={access.isAdmin ? <VenueRelationsScreen /> : <Navigate to="/venues" replace />}
          />
          <Route
            path="growth"
            element={access.isAdmin ? <GrowthHQScreen /> : <Navigate to="/venues" replace />}
          />
          <Route
            path="events"
            element={access.isAdmin ? <EventsScreen /> : <Navigate to="/venues" replace />}
          />
          {/* Venue outreach moved into Growth HQ (growth-agent handoff, 2026-10-12). */}
          <Route path="venue-outreach" element={<Navigate to="/growth" replace />} />
          {/* Retired 2026-09-27 (Evan): the scanner is live standalone at scan.joinshado.com. */}
          <Route path="scanner" element={<Navigate to="/venue-outreach" replace />} />
          {/* Partnerships now lives as a tab inside Venue Portal. Old links still land. */}
          <Route path="partnerships" element={<Navigate to="/venue-outreach" replace />} />
          <Route
            path="venue-descriptions"
            element={access.isAdmin ? <VenueDescriptionsScreen /> : <Navigate to="/venues" replace />}
          />
          <Route
            path="campus-growth"
            element={access.isAdmin ? <CampusGrowthScreen /> : <Navigate to="/venues" replace />}
          />
          <Route
            path="development"
            element={access.isAdmin ? <DevelopmentScreen /> : <Navigate to="/venues" replace />}
          />
          <Route path="dev-timeline" element={<Navigate to="/development" replace />} />
          <Route path="city-overviews" element={<Navigate to="/cities" replace />} />
          <Route path="*" element={<Navigate to={access.isAdmin ? '/' : '/venues'} replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
